import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getKioskAuthorization, kioskPaymentStop } from 'api/Kiosk';

import useError from 'lib/hooks/useError';
import { inRn, snakeToTitleCase } from 'lib/helpers/utility';
import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';

import Modal from 'components/shared/Modal/Modal';
import Loader from 'components/shared/Loader/Loader';
import { showNotification } from 'components/shared/Notification/Notification';
import ErrorPaymentModal from 'components/Payments/NewPayment/PaymentContent/components/ErrorModal';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import Footer from './components/Footer';
import Header from './components/Header';
import CardContent from './components/CardContent';
import Information from './components/Information';

const PayFromKioskModal = ({ isOpen, handleClose, patientId, onSuccess, token }) => {
  const [paymentKind, setPaymentKind] = useState('saved-cards');
  const [cardMessage, setCardMessage] = useState('');

  const { values, terminalData } = usePaymentContext();

  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();
  const navigate = useNavigate();

  const timeoutRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['kiosk-authorization', patientId],
    queryFn: () =>
      getKioskAuthorization(navigate, {
        patientId,
        balance: inRn(values?.amount),
        token: values?.invoiceToken || token,
        processor: terminalData?.cardPresentTerminal?.processor
      }),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0
  });

  // Keep the latest onSuccess without re-creating the socket handler each render.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearTimeoutHandler = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const closeModal = () => {
    handleClose();
    setPaymentKind('saved-cards');
    setErrorMessage(null);
    clearTimeoutHandler();
  };

  const goBack = () => {
    if (paymentKind === 'credit-card') {
      setPaymentKind('saved-cards');
      return;
    }

    closeModal();
  };

  const handleKioskMessage = useCallback(
    (msgData) => {
      switch (msgData?.kind) {
        case 'handlePaymentConfirmationReceived':
          // Intentionally do NOT clear the reconciliation timeout here. The terminal
          // can complete the charge while the live `success` push is missed (e.g. a
          // brief socket reconnect), so the timeout below stays armed to reconcile
          // against the recorded transaction and still resolve the modal.
          break;
        case 'switchToAlternative':
          setPaymentKind('credit-card');
          break;
        case 'success':
          clearTimeoutHandler();
          onSuccessRef.current(msgData?.transactionId);
          break;
        case 'handleCardMessageUpdate':
          setCardMessage(snakeToTitleCase(msgData?.message.toLowerCase()));
          break;
        case 'error':
          if (msgData?.code === 0) {
            setPaymentKind('credit-card');
          } else {
            setCardMessage('Terminal not connected');
            showNotification({
              icon: 'new-info',
              iconColor: 'warning',
              title: 'Terminal not connected'
            });
          }
          break;
        default:
          if (msgData?.transactionId && !msgData?.message) {
            clearTimeoutHandler();
            onSuccessRef.current(msgData?.transactionId);
          }
          break;
      }
    },
    [clearTimeoutHandler]
  );

  useEffect(() => {
    const kAuth = data?.kioskAuthorization;

    try {
      if (!kAuth || kAuth === 'not_configured') return undefined;

      socketOn('kioskPaymentScreenMessage', handleKioskMessage);
      socketEmit('kioskJoinClient', { kioskAuthorization: kAuth }, { reemitOnReconnect: true });

      // Safety net: if the live success/error push never lands, reconcile against
      // the actual transaction so a completed charge still resolves the modal
      // instead of hanging on the last card message.
      timeoutRef.current = setTimeout(async () => {
        const stopData = await kioskPaymentStop(navigate, {});
        if (stopData?.transaction?.status) {
          if (stopData?.transaction?.status === 'completed') {
            clearTimeoutHandler();
            onSuccessRef.current(stopData?.transaction?.id);
          } else if (stopData?.message?.includes('TransactionCardReaderNoneAvailable')) {
            setPaymentKind('credit-card');
          } else {
            setErrorMessage(stopData?.message);
          }
        } else {
          setCardMessage('Terminal not connected');
          showNotification({
            icon: 'new-info',
            iconColor: 'warning',
            title: 'Terminal not connected'
          });
        }
      }, 60000);
    } catch (e) {
      console.error(e);
    }

    return () => {
      socketOff('kioskPaymentScreenMessage', handleKioskMessage);
      clearTimeoutHandler();
    };
  }, [data?.kioskAuthorization, handleKioskMessage]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket.connected]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        width="full"
        title={<Header goBack={goBack} step={paymentKind} />}
        footer={<Footer step={paymentKind} amount={values.amount} onClose={goBack} />}>
        <div className="flex h-full flex-col items-center justify-center">
          <div
            className="flex w-full max-w-[448px] flex-col items-center gap-6 px-4 pb-6 pt-4"
            ref={scrollRef}>
            <Information paymentKind={paymentKind} cardMessage={cardMessage} />
            {isLoading ? (
              <Loader color="primary" size={40} />
            ) : (
              <CardContent
                ref={scrollRef}
                active={paymentKind}
                setActive={setPaymentKind}
                setErrorMessage={setErrorMessage}
                patientId={patientId}
              />
            )}
          </div>
        </div>
      </Modal>
      {error && (
        <ErrorPaymentModal
          message={error}
          onClearError={() => setErrorMessage(null)}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default PayFromKioskModal;
